/* Talks to the Worker. Nothing here blocks the UI: every call returns what it
   got and leaves the caller's cached marks alone on failure (§3.3). */

import type {
  DividendHistory, DividendOutlook, EarningsDate, Financials, FxRates, Logo, MarketState, PerpMark,
  Quote,
} from '../types';

/** Same origin by default — on Cloudflare Pages the Worker is routed at /api/*.
    Set VITE_WORKER_BASE when running the Worker on its own host. */
const BASE = (import.meta.env['VITE_WORKER_BASE'] as string | undefined)?.replace(/\/$/, '') ?? '';

export class ApiError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = 'ApiError';
  }
}

const MARKET_STATES: readonly MarketState[] = ['PRE', 'REGULAR', 'POST', 'POSTPOST', 'CLOSED'];

function asMarketState(v: unknown): MarketState {
  return MARKET_STATES.includes(v as MarketState) ? (v as MarketState) : 'CLOSED';
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { signal, headers: { accept: 'application/json' } });
  if (!res.ok) throw new ApiError(`Request failed (${res.status})`, res.status);
  return res.json() as Promise<T>;
}

/** Symbol lists are chunked so one very large book cannot blow past the Worker's
    per-request ceiling. */
function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

interface Batch<T> { results: T[]; errors: Array<{ symbol: string; error: string }> }

async function batched<Raw, Out>(
  path: string,
  symbols: string[],
  map: (raw: Raw, fetchedAt: string) => Out,
  signal?: AbortSignal,
): Promise<{ rows: Out[]; failed: string[] }> {
  if (symbols.length === 0) return { rows: [], failed: [] };
  const fetchedAt = new Date().toISOString();
  const groups = await Promise.allSettled(
    chunk(symbols, 30).map((group) =>
      // A path may already carry a query — the history endpoint takes a range.
      getJson<Batch<Raw>>(
        `${path}${path.includes('?') ? '&' : '?'}symbols=${encodeURIComponent(group.join(','))}`,
        signal,
      ),
    ),
  );

  const rows: Out[] = [];
  const failed: string[] = [];
  groups.forEach((settled, i) => {
    const group = chunk(symbols, 30)[i] ?? [];
    if (settled.status === 'rejected') {
      failed.push(...group);
      return;
    }
    for (const raw of settled.value.results) rows.push(map(raw, fetchedAt));
    for (const e of settled.value.errors) failed.push(e.symbol);
  });
  return { rows, failed };
}

interface RawQuote {
  symbol: string; price: number; previousClose: number; currency: string;
  exchangeName: string; marketState: string;
  preMarketPrice?: number; postMarketPrice?: number; shortName?: string;
  sessions?: Quote['sessions'];
}

export function fetchQuotes(symbols: string[], signal?: AbortSignal) {
  return batched<RawQuote, Quote>('/api/quote', symbols, (r, fetchedAt) => {
    const q: Quote = {
      symbol: r.symbol,
      price: r.price,
      previousClose: r.previousClose,
      currency: r.currency,
      exchangeName: r.exchangeName,
      marketState: asMarketState(r.marketState),
      fetchedAt,
    };
    if (typeof r.preMarketPrice === 'number') q.preMarketPrice = r.preMarketPrice;
    if (typeof r.postMarketPrice === 'number') q.postMarketPrice = r.postMarketPrice;
    if (r.shortName) q.shortName = r.shortName;
    if (r.sessions) q.sessions = r.sessions;
    return q;
  }, signal);
}

/* The date has to be optional: a company with no scheduled report but a
   dividend to its name comes back carrying only the dividend. */
interface RawCalendar {
  symbol: string;
  date?: string;
  estimated: boolean;
  dividend?: DividendOutlook;
}

/* The dividend rides along on this response rather than costing its own request
   — and used to be dropped right here, on the way in. The Worker sent it, the
   type declared it, the reminders and the dividends screen both read it, and
   this mapping quietly listed three fields out of four. Nothing failed: the
   screens simply had nothing to show and said so, which is why it survived so
   long. Copy the whole row rather than naming fields, so the next field added
   upstream arrives instead of vanishing. */
export function fetchCalendar(symbols: string[], signal?: AbortSignal) {
  return batched<RawCalendar, EarningsDate>('/api/calendar', symbols, (r, fetchedAt) => ({
    ...r, fetchedAt,
  }), signal);
}

interface RawPerp { symbol: string; fairPrice: number; lastPrice: number; indexPrice: number }

export function fetchPerpMarks(symbols: string[], signal?: AbortSignal) {
  return batched<RawPerp, PerpMark>('/api/perp', symbols, (r, fetchedAt) => ({
    symbol: r.symbol, fairPrice: r.fairPrice, lastPrice: r.lastPrice,
    indexPrice: r.indexPrice, fetchedAt,
  }), signal);
}

/* The company's own mark, as bytes. The Worker asks the logo service so the
   device never does — see the note on `/api/logo` in worker/src/index.ts — and
   it comes back base64 so it can be stored as a `data:` URI, which is the only
   image the macOS shell's content policy will draw. */
interface RawLogo {
  symbol: string;
  data?: string;
  mime?: string;
  domain?: string;
  missing?: boolean;
}

export function fetchLogos(symbols: string[], signal?: AbortSignal) {
  return batched<RawLogo, Logo>('/api/logo', symbols, (r, fetchedAt) => ({
    symbol: r.symbol,
    fetchedAt,
    ...(r.domain ? { domain: r.domain } : {}),
    ...(r.data && r.mime
      ? { dataUrl: `data:${r.mime};base64,${r.data}` }
      : { missing: true }),
  }), signal);
}

type RawFinancials = Omit<Financials, 'fetchedAt'>;

/** The income statement and the EPS record. Heavier than a quote and it changes
    once a quarter, so it is fetched only for a position being looked at. */
export function fetchFinancials(symbols: string[], signal?: AbortSignal) {
  return batched<RawFinancials, Financials>('/api/financials', symbols, (r, fetchedAt) => ({
    ...r, fetchedAt,
  }), signal);
}

type RawDividends = Omit<DividendHistory, 'fetchedAt'>;

/** Per-share amounts and pay dates. Half of a dividend record — the share count
    on the day is the user's to supply. */
export function fetchDividendHistory(symbols: string[], signal?: AbortSignal) {
  return batched<RawDividends, DividendHistory>('/api/dividends', symbols, (r, fetchedAt) => ({
    ...r, fetchedAt,
  }), signal);
}

export interface DailyHistory {
  symbol: string;
  currency: string;
  days: Array<{ date: string; close: number }>;
}

/* Closes over a range, for reconstructing a past book value and for drawing one
   company's own line.
   
   `interval` is the bar size. Daily bars come back named by their day; anything
   shorter keeps the whole instant, because a session collapsed to one point is
   not a finer chart. */
export function fetchHistory(
  symbols: string[], range = '3mo', signal?: AbortSignal, interval = '1d',
) {
  if (symbols.length === 0) return Promise.resolve({ rows: [], failed: [] });
  return batched<DailyHistory, DailyHistory>(
    `/api/history?range=${encodeURIComponent(range)}&interval=${encodeURIComponent(interval)}`,
    symbols, (r) => r, signal,
  );
}

export interface SearchHit {
  symbol: string;
  name: string;
  exchange: string;
  type: string;
}

/** What a fragment of a name or ticker actually refers to. Guessing a symbol
    and being wrong prices the wrong company, and nothing on screen says so
    until the position sits there with no mark. */
export async function searchCompanies(q: string, signal?: AbortSignal): Promise<SearchHit[]> {
  if (q.trim().length < 1) return [];
  const r = await getJson<{ results: SearchHit[] }>(
    `/api/search?q=${encodeURIComponent(q.trim())}`, signal,
  );
  return r.results ?? [];
}

export interface ContractHit {
  symbol: string;
  name: string;
  base: string;
  maxLeverage: number | null;
}

/** Contracts on the exchange's board matching a word. Gold is XAU_USDT and oil
    is USOIL_USDT — symbols nobody guesses, which is the point of searching the
    display name as well. */
export async function searchContracts(q: string, signal?: AbortSignal): Promise<ContractHit[]> {
  if (q.trim().length < 1) return [];
  const r = await getJson<{ results: ContractHit[] }>(
    `/api/contracts?q=${encodeURIComponent(q.trim())}`, signal,
  );
  return r.results ?? [];
}

type RawFx = { usdPln: number; eurPln: number; hkdPln?: number; publishedOn: string };

export async function fetchFx(signal?: AbortSignal): Promise<FxRates> {
  const r = await getJson<RawFx>('/api/fx', signal);
  return { ...r, fetchedAt: new Date().toISOString() };
}

/** The table published on a given date. A deposit converts at the rate that
    applied when it landed (§5.1), and deposits are usually entered long after
    the fact — so the rate has to be asked for by date, not taken from today. */
export async function fetchFxOn(on: string, signal?: AbortSignal): Promise<FxRates> {
  const r = await getJson<RawFx>(`/api/fx?on=${encodeURIComponent(on)}`, signal);
  return { ...r, fetchedAt: new Date().toISOString() };
}

/* ---------- retry ---------- */

/** Exponential backoff for the periodic refresh (§3.3). Each failure widens the
    gap; the caller keeps rendering stale marks throughout. */
export class Backoff {
  private failures = 0;
  constructor(private readonly baseMs = 15_000, private readonly maxMs = 10 * 60_000) {}

  get isBackingOff(): boolean { return this.failures > 0; }

  succeed(): void { this.failures = 0; }

  fail(): number {
    this.failures += 1;
    const raw = this.baseMs * 2 ** (this.failures - 1);
    // A little jitter so two devices waking together do not retry in lockstep.
    return Math.min(this.maxMs, raw) * (0.8 + Math.random() * 0.4);
  }
}

/* ---------- blob sync (§6) ---------- */

export interface BlobPayload { ciphertext: string; salt: string; iv: string }

export async function getBlob(
  token?: string,
): Promise<{ blob: BlobPayload; updatedAt: string | null } | null> {
  const res = await fetch(`${BASE}/api/blob`, {
    headers: token ? { 'x-blob-token': token } : {},
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new ApiError(`Could not read the saved copy (${res.status})`, res.status);
  const body = (await res.json()) as { blob: BlobPayload; updatedAt: string | null };
  return { blob: body.blob, updatedAt: body.updatedAt };
}

export type PutBlobResult =
  | { ok: true; updatedAt: string }
  | { ok: false; conflictAt: string };

export async function putBlob(
  blob: BlobPayload,
  opts: { seenAt?: string | null; force?: boolean; token?: string } = {},
): Promise<PutBlobResult> {
  const params = new URLSearchParams();
  if (opts.seenAt) params.set('seenAt', opts.seenAt);
  if (opts.force) params.set('force', '1');
  const qs = params.toString();
  const res = await fetch(`${BASE}/api/blob${qs ? `?${qs}` : ''}`, {
    method: 'PUT',
    headers: {
      'content-type': 'application/json',
      ...(opts.token ? { 'x-blob-token': opts.token } : {}),
    },
    // The envelope goes over as-is: ciphertext, salt and iv, all base64. It was
    // encrypted in the browser and the passphrase never leaves the device (§6),
    // so the Worker is moving bytes it cannot read.
    body: JSON.stringify(blob),
  });
  if (res.status === 409) {
    const body = (await res.json()) as { updatedAt: string };
    return { ok: false, conflictAt: body.updatedAt };
  }
  if (!res.ok) throw new ApiError(`Could not save (${res.status})`, res.status);
  const body = (await res.json()) as { updatedAt: string };
  return { ok: true, updatedAt: body.updatedAt };
}
